import { useCallback, useEffect, useRef, useState, type ComponentType, type SVGProps } from 'react'
import { BotaoIcone } from './BotaoIcone'
import { useFecharAoClicarFora } from './fechar-fora'
import { Flutuante } from './Flutuante'
import { IconeReticencias } from './icones'
import { juntar } from './ui'
import { plural } from '../formato'

/**
 * Barra de ícones que recolhe o que não cabe num menu.
 *
 * Numa janela de 1280px as oito ações do chat cabem lado a lado. Em 640 as
 * duas últimas — transferir e encerrar — saíam inteiras para fora da tela:
 * não eram só apertadas, eram inalcançáveis, e nada na tela dizia que
 * existiam. Medido, não suposto: o botão de encerrar ficava em x=647 numa
 * janela de 640.
 *
 * A medida é do container, não da janela: a coluna de conversas ao lado tem
 * largura fixa, então a mesma janela dá espaços diferentes conforme a lista
 * esteja aberta ou não.
 */

/** 32px do botão + 2px do intervalo. Bate com `h-8 w-8` e `gap-0.5`. */
const LARGURA_DO_BOTAO = 34

export interface AcaoDaBarra {
  chave: string
  rotulo: string
  Icone: ComponentType<SVGProps<SVGSVGElement>>
  aoClicar: () => void
  destaque?: boolean
  perigo?: boolean
  /** Deixa o ícone vermelho já em repouso, e não só no hover. */
  vermelho?: boolean
  /** Um número embaixo do ícone, como quantas pessoas estão de apoio. Zero não aparece. */
  contador?: number
}

export function BarraComTransbordo({ acoes }: { acoes: AcaoDaBarra[] }) {
  const caixa = useRef<HTMLDivElement>(null)
  const [largura, setLargura] = useState(0)
  const [menuAberto, setMenuAberto] = useState(false)
  const fecharMenu = useCallback(() => setMenuAberto(false), [])
  const menu = useFecharAoClicarFora<HTMLDivElement>(menuAberto, fecharMenu)

  useEffect(() => {
    const alvo = caixa.current
    if (!alvo) return
    const observador = new ResizeObserver(([entrada]) => {
      if (entrada) setLargura(entrada.contentRect.width)
    })
    observador.observe(alvo)
    return () => observador.disconnect()
  }, [])

  /*
    Antes da primeira medida, mostra tudo.

    Começar recolhido faria a barra piscar de um menu para os ícones no
    primeiro quadro, em toda abertura de conversa.
  */
  const cabem = largura === 0 ? acoes.length : Math.floor(largura / LARGURA_DO_BOTAO)

  // Se sobra o menu, ele próprio ocupa um lugar.
  const visiveis = cabem >= acoes.length ? acoes : acoes.slice(0, Math.max(0, cabem - 1))
  const recolhidas = acoes.slice(visiveis.length)

  return (
    <div ref={caixa} className="flex min-w-0 flex-1 items-center justify-end gap-0.5">
      {visiveis.map((acao, i) => (
        <BotaoIcone
          key={acao.chave}
          rotulo={acao.rotulo}
          destaque={acao.destaque}
          perigo={acao.perigo}
          onClick={acao.aoClicar}
          // O último da fila encosta na borda: a dica cresce para dentro.
          vermelho={acao.vermelho}
          dicaAlinhada={recolhidas.length === 0 && i === visiveis.length - 1 ? 'direita' : 'centro'}
        >
          <acao.Icone className="h-4 w-4" />
          {acao.contador ? (
            <span className="absolute -bottom-0.5 right-0 inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-marca px-0.5 text-[9px] font-semibold leading-none text-white tabular-nums">
              {acao.contador}
            </span>
          ) : null}
        </BotaoIcone>
      ))}

      {recolhidas.length > 0 ? (
        <div ref={menu} className="relative">
          <BotaoIcone
            rotulo={`Mais ${plural(recolhidas.length, 'ação', 'ações')}`}
            dicaAlinhada="direita"
            onClick={() => setMenuAberto((a) => !a)}
            className={menuAberto ? 'bg-tinta-100 text-tinta-900' : undefined}
          >
            {/*
              Reticências de verdade. Antes era o "+" girado 45°, que na tela
              vira um "×" — lido como "fechar a conversa" justo no botão que
              guarda transferir e encerrar quando a janela é pequena.
            */}
            <IconeReticencias className="h-4 w-4" />
          </BotaoIcone>

          {/* Flutua fora do cabeçalho, para a caixa dele não cortar o menu. */}
          <Flutuante
            ancora={menu}
            aberto={menuAberto}
            aoFechar={fecharMenu}
            largura={208}
            alinhar="fim"
            distancia={4}
            className="overflow-y-auto rounded-xl bg-superficie py-1 shadow-media ring-1 ring-tinta-200"
          >
            <ul>
              {recolhidas.map((acao) => (
                <li key={acao.chave}>
                  <button
                    type="button"
                    onClick={() => {
                      acao.aoClicar()
                      setMenuAberto(false)
                    }}
                    className={juntar(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-tinta-50',
                      acao.vermelho || acao.perigo ? 'text-red-600' : 'text-tinta-600',
                    )}
                  >
                    <acao.Icone className="h-4 w-4 shrink-0" />
                    {acao.rotulo}
                    {acao.contador ? (
                      <span className="ml-auto text-xs text-tinta-400">{acao.contador}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </Flutuante>
        </div>
      ) : null}
    </div>
  )
}
